import { ChevronRight } from "lucide-react";

import { AGENT_CONFIG_METADATA, type AgentConfigSection } from "@prospect/shared";

import { useModels } from "@/api/models";
import type { ModelRow } from "@/api/types";
import { Card } from "@/components/ui/card";

/**
 * Snapshot keys whose value is an Anthropic model id and should be
 * rendered with `KNOWN_MODEL_DISPLAY_NAMES`'s friendly label, raw id
 * on `title=` hover. Two today (`model_id`, `validator_model_id`);
 * the set is the deliberate allowlist rather than a "looks like a
 * model id" pattern match — a future config key that happens to
 * coincide with a model id by accident shouldn't get formatted.
 */
const MODEL_VALUE_KEYS = new Set<string>(["model_id", "validator_model_id"]);

/**
 * Forensic-provenance panel rendered on `/jobs/:id`. One panel per
 * config category — Curator, Runtime, Validator — driven by the
 * `AGENT_CONFIG_METADATA` section taxonomy. Operators answer "what
 * config category drove this run" without drilling into `audit_log`
 * JSON.
 *
 * The mental model is by category, not by agent: each panel reflects
 * a slice of one agent's snapshot, but the operator-facing label is
 * the section name, not the agent type. On a paired curator +
 * validator run, all three panels render side-by-side; the Curator
 * and Runtime panels read from the curator's snapshot, the Validator
 * panel reads from the validator's. Validator-only catch-up runs see
 * just the Validator panel; curator-only runs see Curator + Runtime.
 *
 * Closed by default — the operator opens it when they need to
 * answer the question. The `<details>` element handles keyboard,
 * ARIA, and focus management for free.
 *
 * Cost / Notifications / Catalogue keys never render here — they're
 * global config the operator can't change retrospectively, so per-run
 * surfacing is noise. Operator-added unknown keys also don't render
 * here; they're surfaced separately on `/settings` (which has its
 * own "Other" group for that purpose).
 *
 * Phase 2 (separate follow-up) will overlay diff-against-current
 * chips by fetching `/api/agent-config` alongside; the row structure
 * here is shaped to accept that without rework.
 */

export interface SnapshotRow {
  key: string;
  value: string | number;
  label: string;
  description: string;
}

/** One section panel's data — title + the rows it should render. */
export interface SnapshotPanel {
  /**
   * The metadata section this panel surfaces. Carried alongside
   * `title` because it's the canonical identifier — the call site
   * uses it as a React `key` when mapping panels into JSX, where
   * `title` could in principle drift (capitalisation, future
   * localisation). One field per concern keeps the rename costs
   * cheap.
   */
  section: AgentConfigSection;
  /** Display label, e.g. `"Curator"`. */
  title: string;
  /** Rows already filtered to this section, sorted by `key`. */
  rows: SnapshotRow[];
}

/**
 * Filter a snapshot to one section's rows. Drops keys absent from
 * `AGENT_CONFIG_METADATA` (operator-added or stale) and keys whose
 * section doesn't match — those still surface on `/settings` under
 * "Other" / their own sections, but not on JobDetail.
 *
 * Sorts by `key` so the panel reads deterministically across runs
 * (Sheets row order is operator-controlled and reorders silently).
 *
 * Exported for unit testing.
 */
export function filterSnapshotToSection(
  snapshot: Record<string, string | number>,
  section: AgentConfigSection,
): SnapshotRow[] {
  const rows: SnapshotRow[] = [];
  for (const [key, value] of Object.entries(snapshot)) {
    const meta = AGENT_CONFIG_METADATA[key];
    if (!meta || meta.section !== section) continue;
    rows.push({ key, value, label: meta.label, description: meta.description });
  }
  rows.sort((a, b) => a.key.localeCompare(b.key));
  return rows;
}

export interface AgentConfigSnapshotProps {
  panel: SnapshotPanel;
}

export function AgentConfigSnapshot({ panel }: AgentConfigSnapshotProps) {
  // The snapshot panel is rendered inside `JobDetail`, which is a
  // QueryClient-scoped tree. `useModels()` shares the same registry
  // cache as JobDetail's other consumers — no extra HTTP per panel.
  // Empty `modelsById` (query in flight, or lookup miss) falls back
  // to the raw id below; the cost is one frame of raw text before
  // the friendly label resolves.
  const { modelsById } = useModels();
  return (
    <Card className="p-0">
      <details className="group">
        <summary
          className="list-none cursor-pointer p-6 flex items-start gap-4 [&::-webkit-details-marker]:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
          aria-label={`Config used — ${panel.title}`}
        >
          <ChevronRight
            aria-hidden
            className="h-5 w-5 mt-0.5 text-text-muted transition-transform group-open:rotate-90 shrink-0"
          />
          <div className="flex-1 space-y-1">
            <p className="label-mono">Config used</p>
            <h3 className="font-display text-xl text-text">{panel.title}</h3>
            <p className="text-sm text-text-muted">
              Frozen `agent_config` values driving the {panel.title.toLowerCase()} side of this run.
            </p>
          </div>
        </summary>
        <div className="px-6 pb-6">
          <ul className="space-y-3">
            {panel.rows.map((row) => (
              <SnapshotRowItem key={row.key} row={row} modelsById={modelsById} />
            ))}
          </ul>
        </div>
      </details>
    </Card>
  );
}

function SnapshotRowItem({
  row,
  modelsById,
}: {
  row: SnapshotRow;
  modelsById: Record<string, ModelRow>;
}) {
  return (
    <li className="grid gap-2 sm:grid-cols-[1fr_minmax(180px,260px)] sm:gap-x-8 sm:items-start">
      <div className="space-y-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-text">{row.label}</span>
          <span className="font-mono text-[11px] text-text-faint">{row.key}</span>
        </div>
        {row.description ? (
          <p className="text-xs text-text-muted">{row.description}</p>
        ) : null}
      </div>
      <SnapshotValue rowKey={row.key} value={row.value} modelsById={modelsById} />
    </li>
  );
}

function SnapshotValue({
  rowKey,
  value,
  modelsById,
}: {
  rowKey: string;
  value: string | number;
  modelsById: Record<string, ModelRow>;
}) {
  if (value === "") {
    return <span className="font-mono text-text-faint">—</span>;
  }
  // Model-id keys get the friendly-label treatment with the raw id
  // on `title=` hover — same dual-affordance posture as JobDetail's
  // other model render sites. Lookup miss falls back to the raw id.
  if (MODEL_VALUE_KEYS.has(rowKey) && typeof value === "string") {
    const friendly = modelsById[value]?.displayName ?? value;
    return (
      <span className="font-mono text-sm text-text break-all" title={value}>
        {friendly}
      </span>
    );
  }
  return (
    <span className="font-mono text-sm text-text break-all">
      {String(value)}
    </span>
  );
}
